import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import {
  api, type Artikel, type BedarfDvUebersicht, type Lieferant, type Sammelzettel, type SammelzettelErgebnis, type SammelzettelPos,
} from '../lib/api';
import { MailModal, MAIL_SIGNATUR, type MailDraft } from '../components/MailEntwurf';
import { emailVonLieferant, lieferantVonName } from './Blechbedarf';
import { bestellPdfErzeugen, bestellPdfVorladen, type BestellPdfDaten } from '../lib/bestellPdf';
import { banfEreignisse } from '../lib/banfVerlauf';
import { fmtDatum, fmtMenge } from '../lib/banfFormat';
import { BanfPositionen } from './BanfPositionen';
import { useEinkaufNeu } from '../components/EinkaufNeu';

// Bestellanforderung (BANF), SAP-Sprachgebrauch: ein Beleg je DV (Bezug/Kontierung = unsere interne
// Bestellnummer) mit Positionen (10, 20, 30 …) — aus dem Artikelstamm gewählt oder frei getippt.
// „Bestellung anlegen": je Lieferant eine Bestellung → Bestellung als PDF/Mail. Blech-Bedarfe lassen sich übernehmen.
// (Intern heißt der Beleg weiter `sammelzettel` — Tabellen/API-Pfad unverändert.)

export const banfNr = (id: number) => `BANF-${String(id).padStart(4, '0')}`;
export const posNr = (index: number) => (index + 1) * 10;

// Bestellung als PDF (Briefkopf wie im Kalkulation-Angebot). Keine BANF-Nr./interne Bezüge im Beleg.
export function zettelPdfDaten(lieferant: Lieferant | undefined, name: string, bestellnr: string,
  pos: SammelzettelPos[], nummern: Record<number, number> = {}): BestellPdfDaten {
  return {
    lieferant: lieferant?.name ?? name,
    ansprechpartner: lieferant?.ansprechpartner,
    bestellnr,
    positionen: pos.map((p, i) => ({
      nr: nummern[p.id] ?? posNr(i),
      artikelnr: p.lieferanten_artikelnr,
      bezeichnung: p.bezeichnung,
      menge: fmtMenge(p.menge),
      einheit: p.einheit,
      bemerkung: p.bemerkung,
      liefertermin: p.liefertermin,
    })),
  };
}

// Mail-Text: Das PDF im Anhang enthält die Positionen.
export function zettelMail(_lieferant: string, bestellnr: string, _z: Pick<Sammelzettel, 'id' | 'bezeichnung' | 'dv_nummer'>,
  pos: SammelzettelPos[], email: string, _nummern: Record<number, number> = {}): MailDraft {
  return {
    an: email,
    betreff: `Bestellung ${bestellnr} – ${pos.length} Position${pos.length === 1 ? '' : 'en'}`,
    text: `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie unsere Bestellung Nr. ${bestellnr} (${pos.length} Position${pos.length === 1 ? '' : 'en'}) als PDF.\n\n` +
      'Bitte senden Sie uns eine Auftragsbestätigung mit verbindlichem Liefertermin.' +
      '\n\nVielen Dank.\n\n' + MAIL_SIGNATUR,
  };
}

// --- Lieferant direkt anlegen (Lieferantenstamm) ----------------------------
function LieferantAnlegen({ vorname = '', lieferanten, nurUnbekannt = false, onAngelegt }: {
  vorname?: string; lieferanten: Lieferant[]; nurUnbekannt?: boolean; onAngelegt: (l: Lieferant) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [f, setF] = useState({ name: '', ansprechpartner: '', email: '', telefon: '' });
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const bekannt = !vorname.trim() || lieferanten.some(l => l.name.trim().toLowerCase() === vorname.trim().toLowerCase());
  if (nurUnbekannt && bekannt && !offen) return null;
  const unbekannt = !bekannt;

  function oeffnen() { setF(v => ({ ...v, name: v.name || vorname.trim() })); setFehler(''); setOffen(true); }
  async function speichern() {
    if (!f.name.trim()) { setFehler('Name fehlt'); return; }
    setBusy(true); setFehler('');
    try {
      const r = await api.lieferanten.create({
        name: f.name.trim(), ansprechpartner: f.ansprechpartner.trim() || null,
        email: f.email.trim() || null, telefon: f.telefon.trim() || null,
      });
      onAngelegt(r.data);
      setOffen(false); setF({ name: '', ansprechpartner: '', email: '', telefon: '' });
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }
  return (
    <>
      {unbekannt && !offen && (
        <span className="text-xs text-amber-700" data-testid="lieferant-unbekannt">
          „{vorname.trim()}“ ist nicht im Lieferantenstamm — ohne E-Mail-Adresse kann die Bestellung nicht adressiert werden.
        </span>
      )}
      {!offen && (
        <button type="button" className="text-sm text-brand underline" data-testid="lieferant-neu-btn" onClick={oeffnen}>
          {unbekannt ? '＋ im Stamm anlegen' : '＋ Neuer Lieferant'}
        </button>
      )}
      {offen && (
        <div className="basis-full w-full border border-slate-300 bg-white rounded-lg p-3 mt-1" data-testid="lieferant-form">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Neuer Lieferant (Lieferantenstamm)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input className="input" aria-label="Lieferant Name" placeholder="Name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
            <input className="input" aria-label="Lieferant Ansprechpartner" placeholder="Ansprechpartner" value={f.ansprechpartner} onChange={e => setF({ ...f, ansprechpartner: e.target.value })} />
            <input className="input" aria-label="Lieferant E-Mail" placeholder="E-Mail (für Bestellung)" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
            <input className="input" aria-label="Lieferant Telefon" placeholder="Telefon" value={f.telefon} onChange={e => setF({ ...f, telefon: e.target.value })} />
          </div>
          {fehler && <div className="text-sm text-red-600 mt-2" data-testid="lieferant-fehler">{fehler}</div>}
          <div className="mt-2 flex gap-2">
            <button type="button" className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm disabled:opacity-50" disabled={busy} onClick={speichern}>Lieferant speichern</button>
            <button type="button" className="btn-secondary" onClick={() => setOffen(false)}>Abbrechen</button>
          </div>
        </div>
      )}
    </>
  );
}

// --- Artikelstamm-Suche -----------------------------------------------------
function ArtikelSuche({ onWahl }: { onWahl: (a: Artikel) => void }) {
  const [q, setQ] = useState('');
  const [wg, setWg] = useState('');
  const [gruppen, setGruppen] = useState<{ name: string; n: number }[]>([]);
  const [treffer, setTreffer] = useState<Artikel[]>([]);
  const [offen, setOffen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  // Liste schließt bei Klick/Tippen außerhalb sowie mit Esc (nicht nur nach Auswahl).
  useEffect(() => {
    if (!offen) return;
    const aus = (e: Event) => { if (box.current && !box.current.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', aus); document.addEventListener('touchstart', aus); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', aus); document.removeEventListener('touchstart', aus); document.removeEventListener('keydown', esc); };
  }, [offen]);
  useEffect(() => { api.artikel.gruppen().then(r => setGruppen(r.data.filter(g => g.name !== '__ohne__'))).catch(() => setGruppen([])); }, []);
  // Ohne Suchtext (aber Fokus) erscheinen die meistbestellten Artikel — bei Warengruppe nur diese Gruppe.
  const sucht = q.trim().length >= 2 || wg !== '' || offen;
  useEffect(() => {
    const s = q.trim();
    if (s.length === 1 || !sucht) { setTreffer([]); return; }
    let weg = false;
    const t = setTimeout(() => {
      api.artikel.list({ q: s || undefined, wg: wg || undefined, sort: 'zugriffe', limit: 15 })
        .then(r => { if (!weg) setTreffer(r.data); }).catch(() => { if (!weg) setTreffer([]); });
    }, 250);
    return () => { weg = true; clearTimeout(t); };
  }, [q, wg, sucht]);
  return (
    <div className="relative" ref={box}>
      <div className="flex gap-2">
        <input className="input w-full" placeholder="🔍 Artikelstamm durchsuchen (Bezeichnung, Art.-Nr., z. B. Mutter M8 …)"
               aria-label="Artikel suchen" value={q}
               onChange={e => { setQ(e.target.value); setOffen(true); }} onFocus={() => setOffen(true)} />
        {gruppen.length > 0 && (
          <select className="input w-56" aria-label="Warengruppe" value={wg} onChange={e => { setWg(e.target.value); setOffen(true); }}>
            <option value="">Alle Warengruppen</option>
            {gruppen.map(g => <option key={g.name} value={g.name}>{g.name} ({g.n})</option>)}
          </select>
        )}
      </div>
      {offen && sucht && q.trim().length !== 1 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="artikel-treffer">
          {q.trim() === '' && treffer.length > 0 && <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">Meistbestellt{wg ? ` · ${wg}` : ''}</div>}
          {treffer.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">Kein Treffer — unten frei eintragen, die Position wird im Artikelstamm gespeichert.</div>}
          {treffer.map(a => (
            <button key={a.id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0"
                    onClick={() => { onWahl(a); setQ(''); setWg(''); setOffen(false); setTreffer([]); }}>
              <span className="font-medium">{a.bezeichnung}</span>
              <span className="text-slate-500"> · {a.lieferant ?? '—'}{a.lieferanten_artikelnr ? ` · Art.-Nr. ${a.lieferanten_artikelnr}` : ''}</span>
              {a.zugriffe ? <span className="ml-2 text-xs bg-teal-100 text-teal-800 rounded px-1.5 py-0.5" title="So oft bereits bestellt">{a.zugriffe}× bestellt</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const LEER = { menge: '1', einheit: '', bezeichnung: '', lieferant: '', artnr: '', bemerkung: '' };

export function SammelZettel() {
  const { id: idStr } = useParams();
  const id = Number(idStr);
  // „NEU“ (Einkauf): beim Öffnen der BANF als gesehen markieren, die neuen Positionen bleiben in dieser Ansicht markiert
  const { daten: neuDaten, gesehen: neuGesehen } = useEinkaufNeu();
  const neuRef = useRef<{ zid: number; ids: Set<number> }>({ zid: id, ids: new Set() });
  if (neuRef.current.zid !== id) neuRef.current = { zid: id, ids: new Set() };
  useEffect(() => {
    const meine = neuDaten.banf.filter(p => p.zettel_id === id);
    if (meine.length === 0) return;
    meine.forEach(p => neuRef.current.ids.add(p.id));
    neuGesehen('banf', [id]);
  }, [neuDaten, id, neuGesehen]);
  const res = useApi(() => api.sammelzettel.get(id), [id]);
  const lieferantenRes = useApi(() => api.lieferanten.list(), []);
  const lieferanten: Lieferant[] = lieferantenRes.data?.data ?? [];
  const z = res.data?.data;
  const pos = res.data?.positionen ?? [];
  const aktiv = z && z.status !== 'storniert';
  const darfEntscheiden = !!res.data?.darfEntscheiden;

  const [frageFuer, setFrageFuer] = useState<SammelzettelPos[] | null>(null);
  const [entscheidFuer, setEntscheidFuer] = useState<{ p: SammelzettelPos; art: 'freigeben' | 'ablehnen' } | null>(null);
  const [form, setForm] = useState(LEER);
  const [artikelId, setArtikelId] = useState<number | null>(null);
  const [imStamm, setImStamm] = useState(true);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<{ dv: string; bez: string; notiz: string } | null>(null);
  const [bestellListe, setBestellListe] = useState<SammelzettelPos[] | null>(null);
  const [bedarfOffen, setBedarfOffen] = useState(false);
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  const set = (k: keyof typeof LEER, v: string) => setForm(f => ({ ...f, [k]: v }));

  function artikelGewaehlt(a: Artikel) {
    setArtikelId(a.id);
    setForm(f => ({ ...f, bezeichnung: a.bezeichnung, lieferant: a.lieferant ?? f.lieferant,
      artnr: a.lieferanten_artikelnr ?? '', einheit: a.einheit ?? f.einheit }));
  }

  async function posHinzufuegen() {
    if (!form.bezeichnung.trim()) { setFehler('Kurztext fehlt'); return; }
    setBusy(true); setFehler('');
    try {
      let aid = artikelId;
      if (aid == null && imStamm) {
        const a = await api.artikel.create({
          kategorie: 'katalog', bezeichnung: form.bezeichnung.trim(),
          lieferant: form.lieferant.trim() || undefined, lieferanten_artikelnr: form.artnr.trim() || undefined,
          einheit: form.einheit.trim() || undefined,
        });
        aid = a.data.id;
      }
      await api.sammelzettel.addPos(id, {
        menge: form.menge, einheit: form.einheit, bezeichnung: form.bezeichnung, artikel_id: aid,
        lieferant: form.lieferant, lieferanten_artikelnr: form.artnr, bemerkung: form.bemerkung,
      });
      setForm(LEER); setArtikelId(null);
      res.reload();
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }

  async function kopfSpeichern() {
    if (!edit) return;
    setFehler('');
    try { await api.sammelzettel.update(id, { dv_nummer: edit.dv, bezeichnung: edit.bez, notiz: edit.notiz }); setEdit(null); res.reload(); }
    catch (e) { setFehler((e as Error).message); }
  }

  async function stornieren() {
    const grund = prompt('Grund für das Stornieren der BANF (mind. 5 Zeichen):');
    if (grund == null) return;
    setFehler('');
    try { await api.sammelzettel.storno(id, grund); res.reload(); }
    catch (e) { setFehler((e as Error).message); }
  }

  if (res.loading && !z) return <div className="p-8 text-slate-500">Lädt …</div>;
  if (res.error || !z) return <div className="p-8 text-red-600">Fehler: {res.error?.message ?? 'BANF nicht gefunden'} · <Link className="underline" to="/banf">zurück</Link></div>;

  const wartend = pos.filter(p => p.status === 'offen' && p.rueckfrage === 'wartet');
  const offene = pos.filter(p => p.status === 'offen' && p.rueckfrage !== 'wartet');
  const nummern = Object.fromEntries(pos.map((p, i) => [p.id, posNr(i)]));

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <Link to="/banf" className="text-sm text-slate-500 hover:underline">← alle Bestellanforderungen</Link>
      <header className="mt-2 mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Bestellanforderung (BANF) · Bezug/Kontierung: DV</div>
          {edit ? (
            <div className="flex flex-wrap gap-2 mt-1">
              <input className="input w-40" aria-label="DV-Nr (Bezug)" value={edit.dv} onChange={e => setEdit({ ...edit, dv: e.target.value })} />
              <input className="input w-72" aria-label="Bezeichnung" value={edit.bez} onChange={e => setEdit({ ...edit, bez: e.target.value })} />
              <input className="input w-72" aria-label="Notiz" placeholder="Notiz" value={edit.notiz} onChange={e => setEdit({ ...edit, notiz: e.target.value })} />
              <button className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm" onClick={kopfSpeichern}>Speichern</button>
              <button className="btn-secondary" onClick={() => setEdit(null)}>Abbrechen</button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="zettel-titel">{banfNr(z.id)} · {z.dv_nummer} · {z.bezeichnung}</h1>
              {z.notiz && <p className="text-sm text-slate-600 mt-1">{z.notiz}</p>}
              <p className="text-xs text-slate-500 mt-1">
                {z.ersteller ? `angelegt von ${z.ersteller} · ` : ''}{fmtDatum(z.created_at)} ·{' '}
                <span className={z.status === 'storniert' ? 'text-red-600 font-semibold' : z.status === 'erledigt' ? 'text-emerald-700 font-semibold' : ''}>
                  {z.status === 'offen' ? 'offen' : z.status === 'erledigt' ? 'erledigt (alle Wareneingänge erfolgt)' : `storniert: ${z.storno_grund ?? ''}`}
                </span>
              </p>
            </>
          )}
        </div>
        {aktiv && !edit && (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setEdit({ dv: z.dv_nummer, bez: z.bezeichnung, notiz: z.notiz ?? '' })}>✏ Bearbeiten</button>
            <button className="btn-secondary text-red-600" onClick={stornieren}>Stornieren</button>
          </div>
        )}
      </header>
      {fehler && <div className="mb-3 text-sm text-red-600" data-testid="zettel-fehler">{fehler}</div>}
      {wartend.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="rueckfrage-banner">
          ⏳ <b>{wartend.length} Position{wartend.length === 1 ? '' : 'en'} warten auf die Freigabe vom Chef</b>
          {darfEntscheiden ? ' — bitte unten je Position freigeben oder ablehnen.' : ' — sie sind erst nach der Freigabe bestellbar.'}
        </div>
      )}

      <BanfPositionen neuIds={neuRef.current.ids} zettelId={id} dv={z.dv_nummer} pos={pos} nummern={nummern} aktiv={!!aktiv} darfEntscheiden={darfEntscheiden}
                      lieferanten={lieferanten} onReload={res.reload} onFehler={setFehler}
                      onFrage={ps => setFrageFuer(ps)} onAblehnen={p => setEntscheidFuer({ p, art: 'ablehnen' })} onBestellen={ps => setBestellListe(ps)} />

      {aktiv && (
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="pos-form">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Position anlegen</div>
          <ArtikelSuche onWahl={artikelGewaehlt} />
          <datalist id="zettel-lieferanten">{lieferanten.map(l => <option key={l.id} value={l.name} />)}</datalist>
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2 mt-2">
            <input className="input md:col-span-1" aria-label="Menge" placeholder="Menge" value={form.menge} onChange={e => set('menge', e.target.value)} />
            <input className="input md:col-span-1" aria-label="Einheit" placeholder="ME (Stk, m …)" value={form.einheit} onChange={e => set('einheit', e.target.value)} />
            <input className="input col-span-2 md:col-span-4" aria-label="Bezeichnung der Position" placeholder="Kurztext (z. B. Schraube M8x30 DIN 933) *"
                   value={form.bezeichnung} onChange={e => { set('bezeichnung', e.target.value); setArtikelId(null); }} />
            <input className="input md:col-span-2" aria-label="Lieferanten-Art.-Nr." placeholder="Lieferanten-Art.-Nr." value={form.artnr} onChange={e => set('artnr', e.target.value)} />
            <input className="input md:col-span-2" list="zettel-lieferanten" aria-label="Lieferant der Position" placeholder="Lieferant"
                   value={form.lieferant} onChange={e => set('lieferant', e.target.value)} />
            <input className="input col-span-2 md:col-span-2" aria-label="Bemerkung" placeholder="Bemerkung" value={form.bemerkung} onChange={e => set('bemerkung', e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <button className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
                    disabled={busy} onClick={posHinzufuegen}>+ Position</button>
            <LieferantAnlegen vorname={form.lieferant} lieferanten={lieferanten}
              onAngelegt={l => { set('lieferant', l.name); lieferantenRes.reload(); }} />
            {artikelId == null ? (
              <label className="text-sm text-slate-600 flex items-center gap-2">
                <input type="checkbox" checked={imStamm} onChange={e => setImStamm(e.target.checked)} />
                📚 Im Artikelstamm speichern (beim nächsten Mal auswählbar)
              </label>
            ) : <span className="text-sm text-emerald-700">📚 Aus Artikelstamm gewählt</span>}
            <button className="text-sm text-slate-500 underline ml-auto" onClick={() => setBedarfOffen(o => !o)}>
              {bedarfOffen ? '▾' : '▸'} Blech-Bedarfe zu dieser DV übernehmen
            </button>
          </div>
          {bedarfOffen && <AusBedarf zettel={z} onFertig={() => { setBedarfOffen(false); res.reload(); }} />}
        </section>
      )}

      {aktiv && (
        <div className="flex justify-end mb-6">
          <button data-testid="zettel-bestellen" disabled={offene.length === 0} onClick={() => setBestellListe(offene)}
                  className="bg-brand text-white rounded-lg px-6 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 shadow-sm">
            📦 Bestellung anlegen ({offene.length} offene Position{offene.length === 1 ? '' : 'en'}{wartend.length ? ` · ${wartend.length} beim Chef` : ''})
          </button>
        </div>
      )}

      <BestellKarten pos={pos} nummern={nummern} zettelId={id} />

      <button className="text-sm text-slate-500 underline" onClick={() => setVerlaufOffen(o => !o)}>{verlaufOffen ? '▾' : '▸'} Verlauf</button>
      {verlaufOffen && <Verlauf id={id} nummern={nummern} />}

      {frageFuer && (
        <TextModal titel="Beim Chef nachfragen" testid="frage-modal" okText="Frage senden" pflicht
          hinweis={frageFuer.length === 1
            ? <><b>{frageFuer[0]!.bezeichnung}</b> ({fmtMenge(frageFuer[0]!.menge)} {frageFuer[0]!.einheit ?? ''}) wird zurückgestellt und ist erst nach der Freigabe bestellbar.</>
            : <><b>{frageFuer.length} Positionen</b> werden mit derselben Frage zurückgestellt und sind erst nach der Freigabe bestellbar.</>}
          label="Frage / Anmerkung an den Chef" platzhalter="z. B. Preis 180 € — bestellen?"
          onClose={() => setFrageFuer(null)}
          onOk={async t => { for (const p of frageFuer) await api.sammelzettel.frage(p.id, t); setFrageFuer(null); res.reload(); }} />
      )}
      {entscheidFuer && (
        <TextModal titel="Position ablehnen" testid="ablehnen-modal" okText="Ablehnen" pflicht danger
          hinweis={<><b>{entscheidFuer.p.bezeichnung}</b> wird storniert. Der Grund ist für Einkauf und Besteller sichtbar.</>}
          label="Grund der Ablehnung" platzhalter="z. B. zu teuer, anderes Teil nehmen"
          onClose={() => setEntscheidFuer(null)}
          onOk={async t => { await api.sammelzettel.entscheid(entscheidFuer.p.id, 'ablehnen', t); setEntscheidFuer(null); res.reload(); }} />
      )}

      {bestellListe && (
        <ZettelBestellModal zettel={z} offene={bestellListe} lieferanten={lieferanten}
                            nummern={Object.fromEntries(pos.map((p, i) => [p.id, posNr(i)]))}
                            onLieferantAngelegt={() => lieferantenRes.reload()}
                            onClose={() => { setBestellListe(null); res.reload(); }} />
      )}
    </div>
  );
}

function wannFmt(w: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(w);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : w;
}

function Verlauf({ id, nummern = {} }: { id: number; nummern?: Record<number, number> }) {
  const h = useApi(() => api.sammelzettel.history(id), [id]);
  const ev = banfEreignisse(h.data?.data ?? [], pid => (nummern[pid] != null ? `Pos. ${nummern[pid]}` : `Pos. #${pid}`));
  return (
    <ul className="mt-2 text-xs text-slate-600 space-y-1" data-testid="zettel-verlauf">
      {ev.length === 0 && <li>{h.loading ? 'Lädt …' : 'Noch kein Verlauf.'}</li>}
      {ev.map(r => (
        <li key={r.id + r.text}><span className="text-slate-400 tabular-nums">{wannFmt(r.wann)}</span> · <b>{r.wer}</b> · {r.text}</li>
      ))}
    </ul>
  );
}

// Bestellkarten: je Lieferant + Bestell-Nr. eine Karte mit Status, Positionen und eigenem Verlauf.
function BestellKarten({ pos, nummern, zettelId }: { pos: SammelzettelPos[]; nummern: Record<number, number>; zettelId: number }) {
  const bestellte = pos.filter(p => p.status === 'bestellt' || p.status === 'geliefert');
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  const hist = useApi(() => api.sammelzettel.history(zettelId), [zettelId, pos.length, bestellte.filter(p => p.status === 'geliefert').length], Object.values(offen).some(Boolean));
  if (bestellte.length === 0) return null;
  const gruppen = new Map<string, SammelzettelPos[]>();
  for (const p of bestellte) {
    const k = `${(p.lieferant ?? '').trim()}|${p.bestellnr ?? ''}|${p.bestell_datum ?? ''}`;
    gruppen.set(k, [...(gruppen.get(k) ?? []), p]);
  }
  const ev = banfEreignisse(hist.data?.data ?? [], pid => (nummern[pid] != null ? `Pos. ${nummern[pid]}` : `Pos. #${pid}`));
  return (
    <section className="mb-6 space-y-3" data-testid="bestell-karten">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Bestellungen ({gruppen.size})</div>
      {[...gruppen.entries()].map(([k, ps]) => {
        const geliefert = ps.filter(p => p.status === 'geliefert').length;
        const komplett = geliefert === ps.length;
        const chip = komplett ? { t: '✅ Wareneingang komplett', c: 'bg-emerald-100 text-emerald-800' }
          : geliefert > 0 ? { t: `teilweise geliefert (${geliefert}/${ps.length})`, c: 'bg-amber-100 text-amber-800' }
          : { t: '📦 bestellt — Ware erwartet', c: 'bg-sky-100 text-sky-800' };
        const p0 = ps[0]!;
        const ids = new Set(ps.map(p => p.id));
        const mein = ev.filter(e => e.posIds.some(i => ids.has(i)));
        const lt = [...new Set(ps.map(p => (p.liefertermin ?? '').trim()).filter(Boolean))].join(', ');
        return (
          <div key={k} className="bg-white rounded-xl border border-sky-200 shadow-sm" data-testid="bestell-karte">
            <div className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="font-semibold text-slate-900">📦 Bestellung · {p0.lieferant ?? '—'} · Best.-Nr. {p0.bestellnr ?? '—'}</div>
              <span className={`text-xs rounded-full px-2.5 py-0.5 font-medium ${chip.c}`} data-testid="bestell-chip">{chip.t}</span>
              <div className="text-xs text-slate-500 ml-auto">bestellt {fmtDatum(p0.bestell_datum)} von {p0.besteller ?? '—'} · Liefertermin: {lt || '—'}</div>
            </div>
            <ul className="px-4 pb-2 text-[13px] divide-y divide-slate-100">
              {ps.map(p => (
                <li key={p.id} className="py-1.5 flex gap-3">
                  <span className="text-slate-400 w-8">{nummern[p.id] ?? ''}</span>
                  <span className="whitespace-nowrap">{fmtMenge(p.menge)} {p.einheit ?? ''}</span>
                  <span className="flex-1">{p.bezeichnung}{p.lieferanten_artikelnr ? <span className="text-slate-400"> · {p.lieferanten_artikelnr}</span> : null}</span>
                  <span className={p.status === 'geliefert' ? 'text-emerald-700' : 'text-sky-700'}>{p.status === 'geliefert' ? '✅ da' : '○ erwartet'}</span>
                </li>
              ))}
            </ul>
            <div className="px-4 pb-3">
              <button className="text-sm text-slate-500 underline" aria-label={`Verlauf der Bestellung ${p0.lieferant ?? ''} ${p0.bestellnr ?? ''}`}
                      onClick={() => setOffen(o => ({ ...o, [k]: !o[k] }))}>{offen[k] ? '▾' : '▸'} Verlauf</button>
              {offen[k] && (
                <ul className="mt-2 text-xs text-slate-600 space-y-1" data-testid="karten-verlauf">
                  {mein.length === 0 && <li>{hist.loading ? 'Lädt …' : 'Noch kein Verlauf.'}</li>}
                  {mein.map(r => <li key={r.id + r.text}><span className="text-slate-400 tabular-nums">{wannFmt(r.wann)}</span> · <b>{r.wer}</b> · {r.text}</li>)}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

// Kleines Textfeld-Modal (Rückfrage / Ablehnung)
function TextModal({ titel, hinweis, label, platzhalter, okText, pflicht, danger, testid, onOk, onClose }: {
  titel: string; hinweis: React.ReactNode; label: string; platzhalter: string; okText: string; pflicht?: boolean; danger?: boolean; testid: string;
  onOk: (text: string) => Promise<void>; onClose: () => void;
}) {
  const [t, setT] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  async function los() {
    if (pflicht && t.trim().length < 3) { setFehler('Bitte kurz eintragen (mind. 3 Zeichen).'); return; }
    setBusy(true); setFehler('');
    try { await onOk(t.trim()); } catch (e) { setFehler((e as Error).message); setBusy(false); }
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-16" onClick={e => e.stopPropagation()} data-testid={testid}>
        <div className="px-6 py-4 border-b border-slate-200 font-semibold">{titel}</div>
        <div className="p-6 space-y-3 text-sm">
          <p className="text-slate-600">{hinweis}</p>
          <label className="block">{label}
            <textarea className="input w-full mt-1 min-h-[80px]" aria-label={label} placeholder={platzhalter} value={t} onChange={e => setT(e.target.value)} autoFocus />
          </label>
          {fehler && <div className="text-red-700" data-testid="textmodal-fehler">{fehler}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
            <button className={`${danger ? 'bg-red-600' : 'bg-brand'} text-white rounded-lg px-4 py-2 font-medium hover:opacity-90 disabled:opacity-50`} disabled={busy} onClick={los}>{okText}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AusBedarf({ zettel, onFertig }: { zettel: Sammelzettel; onFertig: () => void }) {
  const [dv, setDv] = useState(zettel.dv_nummer);
  const [rows, setRows] = useState<BedarfDvUebersicht['data'] | null>(null);
  const [wahl, setWahl] = useState<Record<number, boolean>>({});
  const [fehler, setFehler] = useState('');
  async function suchen() {
    setFehler('');
    try {
      const r = await api.blechbedarf.dv(dv, true);
      const offen = r.data.filter(x => !x.bestellt && !x.fertig);
      setRows(offen); setWahl(Object.fromEntries(offen.map(x => [x.id, true])));
    } catch (e) { setFehler((e as Error).message); }
  }
  async function uebernehmen() {
    const ids = Object.entries(wahl).filter(([, v]) => v).map(([k]) => Number(k));
    if (!ids.length) return;
    try { await api.sammelzettel.ausBedarf(zettel.id, ids); onFertig(); }
    catch (e) { setFehler((e as Error).message); }
  }
  return (
    <div className="mt-3 border-t border-slate-200 pt-3" data-testid="aus-bedarf">
      <div className="flex gap-2">
        <input className="input w-56" aria-label="DV im Bedarf" value={dv} onChange={e => setDv(e.target.value)} />
        <button className="btn-secondary" onClick={suchen}>Suchen</button>
      </div>
      {fehler && <div className="text-sm text-red-600 mt-2">{fehler}</div>}
      {rows && rows.length === 0 && <div className="text-sm text-slate-500 mt-2">Keine offenen Blech-Bedarfe mit dieser DV.</div>}
      {rows && rows.length > 0 && (
        <>
          <ul className="mt-2 text-sm space-y-1">
            {rows.map(r => (
              <li key={r.id}>
                <label className="flex gap-2 items-center">
                  <input type="checkbox" aria-label={`Bedarf ${r.id} wählen`} checked={!!wahl[r.id]} onChange={e => setWahl(w => ({ ...w, [r.id]: e.target.checked }))} />
                  {r.menge ?? '—'} × {[r.form, r.werkstoff, r.format, r.dicke ? `${r.dicke} mm` : null].filter(Boolean).join(' · ') || '—'}
                  <span className="text-slate-400">· {r.lieferant ?? 'ohne Lieferant'}</span>
                </label>
              </li>
            ))}
          </ul>
          <button className="mt-2 bg-brand text-white rounded-lg px-4 py-1.5 text-sm" onClick={uebernehmen}>Auf den Zettel übernehmen</button>
        </>
      )}
    </div>
  );
}

function ZettelBestellModal({ zettel, offene, lieferanten, nummern, onLieferantAngelegt, onClose }: {
  zettel: Sammelzettel; offene: SammelzettelPos[]; lieferanten: Lieferant[];
  nummern: Record<number, number>; onLieferantAngelegt: (l: Lieferant) => void; onClose: () => void;
}) {
  const [wahl, setWahl] = useState<Record<number, boolean>>(() => Object.fromEntries(offene.map(p => [p.id, !!(p.lieferant ?? '').trim()])));
  const [lief, setLief] = useState<Record<number, string>>(() => Object.fromEntries(offene.map(p => [p.id, p.lieferant ?? ''])));
  const [kopf, setKopf] = useState<Record<string, { bestellnr?: string; liefertermin?: string }>>({});
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [ergebnis, setErgebnis] = useState<SammelzettelErgebnis | null>(null);
  const [mail, setMail] = useState<MailDraft | null>(null);
  const [mailPdf, setMailPdf] = useState<BestellPdfDaten | null>(null);
  useEffect(() => { bestellPdfVorladen(); }, []);

  const gewaehlt = offene.filter(p => wahl[p.id]);
  const ohneLieferant = gewaehlt.filter(p => !(lief[p.id] ?? '').trim());
  const gruppen = new Map<string, { name: string; rows: SammelzettelPos[] }>();
  for (const p of gewaehlt) {
    const name = (lief[p.id] ?? '').trim(); if (!name) continue;
    const key = name.toLowerCase();
    const g = gruppen.get(key) ?? { name, rows: [] };
    g.rows.push(p); gruppen.set(key, g);
  }
  const gruppenListe = [...gruppen.entries()];
  const setK = (key: string, f: 'bestellnr' | 'liefertermin', v: string) => setKopf(k => ({ ...k, [key]: { ...k[key], [f]: v } }));

  async function bestellen() {
    if (gewaehlt.length === 0 || ohneLieferant.length > 0) return;
    const t = gruppenListe.map(([, g]) => `${g.name}: ${g.rows.length} Position${g.rows.length === 1 ? '' : 'en'}`).join('\n');
    if (!confirm(`Bestellung(en) jetzt anlegen?\n\n${t}\n\nDie Positionen werden als „bestellt“ markiert (Rückgängig: Position → zurücksetzen).`)) return;
    setBusy(true); setFehler('');
    try {
      const r = await api.sammelzettel.bestellen(zettel.id, gruppenListe.map(([key, g]) => ({
        lieferant: g.name, ids: g.rows.map(x => x.id),
        bestellnr: (kopf[key]?.bestellnr ?? zettel.dv_nummer).trim() || undefined,
        liefertermin: kopf[key]?.liefertermin?.trim() || undefined,
      })));
      setErgebnis(r);
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }

  return (
    <>
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8" data-testid="zettel-modal" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">📦 Bestellung anlegen · {banfNr(zettel.id)} · DV {zettel.dv_nummer} · {zettel.bezeichnung}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Schließen">×</button>
        </div>
        {ergebnis ? (
          <div className="p-6 space-y-3" data-testid="zettel-ergebnis">
            <div className="text-emerald-800 font-semibold">✅ {ergebnis.data.length} Bestellung{ergebnis.data.length === 1 ? '' : 'en'} angelegt ({ergebnis.anzahl} Position{ergebnis.anzahl === 1 ? '' : 'en'} bestellt)</div>
            <p className="text-sm text-slate-600">Gespeichert. Jetzt die Bestellung je Lieferant als Mail/PDF erzeugen und in Outlook abschicken — es wird nichts automatisch versendet.</p>
            {ergebnis.data.map(g => (
              <div key={g.lieferant} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-4 py-2">
                <div className="text-sm"><b>{g.lieferant}</b> · {g.positionen.length} Position{g.positionen.length === 1 ? '' : 'en'} · Best.-Nr {g.bestellnr}</div>
                <button className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:opacity-90"
                        onClick={() => {
                          setMailPdf(zettelPdfDaten(lieferantVonName(lieferanten, g.lieferant), g.lieferant, g.bestellnr, g.positionen, nummern));
                          setMail(zettelMail(g.lieferant, g.bestellnr, zettel, g.positionen, emailVonLieferant(lieferanten, g.lieferant), nummern));
                        }}>
                  ✉ Bestellung (PDF / Mail)
                </button>
              </div>
            ))}
            <div className="flex justify-end pt-2"><button className="btn-secondary" onClick={onClose}>Schließen</button></div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">Positionen ankreuzen, Lieferant prüfen — je Lieferant wird eine Bestellung angelegt. Die Bestellnummer ist standardmäßig die DV-Nr.</p>
              <datalist id="zb-lieferanten">{lieferanten.map(l => <option key={l.id} value={l.name} />)}</datalist>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-100 text-xs text-slate-600 uppercase">
                    <tr>{['', 'Pos.', 'Menge / ME', 'Kurztext', 'Lieferanten-Art.-Nr.', 'Lieferant'].map(h => <th key={h} className="text-left px-2.5 py-1.5 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {offene.map(p => (
                      <tr key={p.id} className={wahl[p.id] ? 'bg-emerald-50/50' : ''}>
                        <td className="px-2.5 py-1.5"><input type="checkbox" aria-label={`Position ${p.id} wählen`} checked={!!wahl[p.id]}
                                                              onChange={e => setWahl(w => ({ ...w, [p.id]: e.target.checked }))} /></td>
                        <td className="px-2.5 py-1.5 text-slate-400">{nummern[p.id]}</td>
                        <td className="px-2.5 py-1.5 whitespace-nowrap">{fmtMenge(p.menge)} {p.einheit ?? ''}</td>
                        <td className="px-2.5 py-1.5">{p.bezeichnung}</td>
                        <td className="px-2.5 py-1.5">{p.lieferanten_artikelnr ?? '—'}</td>
                        <td className="px-2.5 py-1.5">
                          <input className="input w-44" list="zb-lieferanten" aria-label={`Lieferant Position ${p.id}`} placeholder="Lieferant …"
                                 value={lief[p.id] ?? ''}
                                 onChange={e => { const v = e.target.value; const warLeer = !(lief[p.id] ?? '').trim(); setLief(l => ({ ...l, [p.id]: v })); if (v.trim() && warLeer) setWahl(w => ({ ...w, [p.id]: true })); }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ohneLieferant.length > 0 && <div className="text-xs text-red-600" data-testid="zb-ohne-lieferant">{ohneLieferant.length} gewählte Position{ohneLieferant.length === 1 ? ' hat' : 'en haben'} noch keinen Lieferant.</div>}
              {gruppenListe.length > 0 && (
                <div className="space-y-2" data-testid="zb-gruppen">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Bestellungen ({gruppenListe.length}) — je Lieferant eine</div>
                  {gruppenListe.map(([key, g]) => (
                    <div key={key} className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-lg px-4 py-2">
                      <div className="text-sm font-semibold min-w-[12rem]">{g.name} <span className="font-normal text-slate-500">· {g.rows.length} Position{g.rows.length === 1 ? '' : 'en'}</span></div>
                      <LieferantAnlegen vorname={g.name} lieferanten={lieferanten} nurUnbekannt onAngelegt={onLieferantAngelegt} />
                      <label className="text-xs text-slate-500">Bestellnummer
                        <input className="input ml-2 w-40" aria-label={`Bestell-Nr ${g.name}`} value={kopf[key]?.bestellnr ?? zettel.dv_nummer}
                               onChange={e => setK(key, 'bestellnr', e.target.value)} /></label>
                      <label className="text-xs text-slate-500">Liefertermin (optional)
                        <input className="input ml-2 w-36" aria-label={`Liefertermin ${g.name}`} placeholder="z. B. KW 44" value={kopf[key]?.liefertermin ?? ''}
                               onChange={e => setK(key, 'liefertermin', e.target.value)} /></label>
                    </div>
                  ))}
                </div>
              )}
              {fehler && <div className="text-sm text-red-600">{fehler}</div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
              <button onClick={bestellen} data-testid="zb-bestellen" disabled={busy || gewaehlt.length === 0 || ohneLieferant.length > 0}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {busy ? 'Legt an …' : `Bestellung anlegen (${gewaehlt.length} Position${gewaehlt.length === 1 ? '' : 'en'}, ${gruppenListe.length} Lieferant${gruppenListe.length === 1 ? '' : 'en'})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
    {mail && <MailModal draft={mail} onChange={setMail} onClose={() => setMail(null)} titel="✉ Bestellung an Lieferant"
                        pdf={mailPdf ? () => bestellPdfErzeugen(mailPdf) : undefined} />}
    </>
  );
}
